// src/pages/DocumentViewer.tsx: standalone page that renders a generated
// agreement or invoice.
//
// This is what emailed document links point at. Linking straight to a signed
// storage URL made the browser download the raw HTML instead of showing it, so
// the reader never saw the document. Here the HTML is regenerated from the
// application row and rendered in an iframe, which also means the reader always
// sees the current terms rather than a stale stored copy.
import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabase';
import { C, ManagerApplication } from '../lib/applications';
import {
  generateContractHTML, generateInvoiceHTML, contractDataFromApp, invoiceDataFromApp,
} from '../lib/generatePDF';
import { fetchDocHtml } from '../lib/applicationDocs';

const DOC_TYPES = ['contract', 'invoice', 'contract-signed'] as const;
type DocType = typeof DOC_TYPES[number];

const TITLES: Record<DocType, string> = {
  'contract': 'Platform Access Agreement',
  'invoice': 'Invoice',
  'contract-signed': 'Signed Agreement',
};

const toolbarBtn: React.CSSProperties = {
  background: 'rgba(255,255,255,0.1)',
  border: '1px solid rgba(255,255,255,0.2)',
  color: '#FFFFFF',
  padding: '6px 16px',
  borderRadius: '3px',
  cursor: 'pointer',
  fontSize: '12px',
  fontFamily: 'system-ui',
  whiteSpace: 'nowrap',
};

const notice = (message: string) =>
  `<!DOCTYPE html><html><head><meta charset="utf-8"></head>` +
  `<body style="font-family:system-ui,-apple-system,sans-serif;color:#6B7280;` +
  `padding:80px 40px;text-align:center;font-size:14px;">${message}</body></html>`;

/** Due date shown on the invoice: 30 days from when the contract was sent. */
function dueDateFor(app: ManagerApplication): string {
  const sent = app.contract_sent_at ? new Date(app.contract_sent_at) : new Date();
  return new Date(sent.getTime() + 30 * 86400000).toISOString().slice(0, 10);
}

export default function DocumentViewer() {
  const { applicationId, docType } = useParams<{ applicationId: string; docType: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [html, setHtml] = useState<string | null>(null);
  const [firmName, setFirmName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const kind = (DOC_TYPES as readonly string[]).includes(docType || '') ? (docType as DocType) : null;

  useEffect(() => {
    if (authLoading || !user || !applicationId || !kind) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      // RLS scopes this: an applicant sees only their own row, an admin sees all.
      const { data, error: err } = await supabase.from('manager_applications')
        .select('*').eq('id', applicationId).maybeSingle();
      if (cancelled) return;
      if (err) { setError(err.message); setLoading(false); return; }
      if (!data) {
        setError('This document is not available on your account.');
        setLoading(false);
        return;
      }
      const app = data as ManagerApplication;
      setFirmName(app.firm_name);

      if (kind === 'contract-signed') {
        // The countersigned copy embeds the drawn signature, which only exists
        // in the stored file, so it cannot be regenerated.
        const stored = await fetchDocHtml(app.id, 'contract-signed');
        if (cancelled) return;
        if (!stored) setError('The signed agreement could not be loaded.');
        setHtml(stored);
      } else if (kind === 'invoice') {
        setHtml(generateInvoiceHTML(invoiceDataFromApp(app, dueDateFor(app))));
      } else {
        setHtml(generateContractHTML(contractDataFromApp(app)));
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [authLoading, user, applicationId, kind]);

  const handlePrint = () => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.focus();
    win.print();
  };

  const handleDownload = () => {
    if (!html) return;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(TITLES[kind || 'contract'] + ' ' + firmName).trim().replace(/\s+/g, '-').toLowerCase()}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (authLoading) return null;

  // Send an unauthenticated reader to sign in, then straight back here. Coming
  // from an email link, this is the common path.
  if (!user) {
    const back = encodeURIComponent(`/documents/${applicationId}/${docType}`);
    return <Navigate to={`/login?redirect=${back}`} replace />;
  }

  if (!kind) return <Navigate to="/application-status" replace />;

  const title = `${TITLES[kind]}${firmName ? `, ${firmName}` : ''}`;

  return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', background: '#525659' }}>

      {/* Toolbar */}
      <div style={{
        background: C.navy, padding: '12px 24px', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
          <svg width="26" height="26" viewBox="0 0 28 28" fill="none" style={{ flexShrink: 0 }}>
            <path d="M 4 14 A 10 10 0 0 1 24 14" stroke="#5dc9a8" strokeWidth="2.2" strokeLinecap="round" />
            <path d="M 6 14 A 8 8 0 0 0 22 14" stroke={C.gold} strokeWidth="2.2" strokeLinecap="round" />
            <circle cx="14" cy="14" r="2.2" fill={C.gold} />
          </svg>
          <span style={{
            color: C.white, fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '15px',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{title}</span>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexShrink: 0 }}>
          <button onClick={handlePrint} disabled={!html} style={{ ...toolbarBtn, opacity: html ? 1 : 0.5 }}>
            Print / Save PDF
          </button>
          <button onClick={handleDownload} disabled={!html} style={{ ...toolbarBtn, opacity: html ? 1 : 0.5 }}>
            Download
          </button>
          <button
            onClick={() => navigate('/application-status')}
            style={{
              background: C.gold, border: 'none', color: C.navy,
              padding: '6px 16px', borderRadius: '3px', cursor: 'pointer',
              fontSize: '13px', fontWeight: 700, fontFamily: 'system-ui', whiteSpace: 'nowrap',
            }}
          >Back to Portal</button>
        </div>
      </div>

      {/* Document */}
      <iframe
        ref={iframeRef}
        srcDoc={html || notice(loading ? 'Loading document...' : error || 'Document unavailable.')}
        style={{ flex: 1, border: 'none', background: C.white, width: '100%' }}
        title={title}
      />
    </div>
  );
}
